import type { PortfolioItem } from '@freelancer/datastore/collections';
import type { Margin } from '@freelancer/ui/margin';
import type { FontWeight, TextSize } from '@freelancer/ui/text';
import type { ResourceTypeApi } from 'api-typings/resources/metadata';
import type { FormCondition, FormQuestionKeyApi } from './logic.model';

export interface Form {
  readonly id: number;
  // Indicates this is a two column form
  readonly twoColumn?: boolean;
  // Sections for the steps in the two column PJP header
  readonly headerStepSections?: readonly HeaderStepSection[];
  readonly sections: readonly FormSection[];
}

/**
 * A section of the form which can group multiple questions under a specific heading and layout.
 */
export interface FormSection {
  readonly id: number;
  readonly name: FormSectionNameApi;
  readonly col?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  readonly margin?: Margin;
  readonly topMargin?: Margin;
  readonly heading?: string;
  /* Optional text (right of heading) */
  readonly headingSmallText?: string;
  /* Optional tooltip (right of heading) */
  readonly headingTooltip?: string;
  /* Optional description paragraph (under the heading) */
  readonly description?: string;
  readonly backgroundConfig?: BackgroundConfig;
  readonly questions: readonly FormQuestion[];
}

export interface BackgroundConfig {
  // Indicate that this section should have a dark background
  readonly darkBackground?: boolean;
  // Indicates that darkBackground is conditional
  readonly conditions?: readonly FormCondition[];
}

/**
 * The corresponding section for a specific step of the steps sidebar
 */
export interface HeaderStepSection {
  readonly id: number;
  readonly headerStep: FormHeaderStepNameApi;
  readonly headerStepDisplayName: string;
  readonly heading?: string;
  /**
   * When there are multiple sections that can be attached to a header step, these are the
   * conditions to determine which one is displayed.
   * Conditions should not overlap (ie. Only one section's conditions should be met at any time).
   *
   * Currently only handles `shown: true` conditions.
   */
  readonly conditions?: readonly FormCondition[];
  readonly questions: readonly FormQuestion[];
}

/**
 * This has extra configuration options to be defined depending on its `type`.
 */
export interface FormQuestion {
  readonly id: number;
  readonly type: FormInputTypeApi;
  readonly name: FormQuestionKeyApi;
  /* Column width for the question */
  readonly col?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  /* Label text */
  readonly text?: string;
  /* Label text weight */
  readonly weight?: FontWeight;
  /* Label text size */
  readonly size?: TextSize;
  /* Tooltip text (tooltip is right of label) */
  readonly tooltipText?: string;
  /* Whether the question's label is inline with the input */
  readonly inline?: boolean;
  readonly placeholder?: string;
  /* This may be overwritten by draft if one exists */
  readonly defaultValue?: any;
  readonly answerOptions?: readonly FormAnswerOption[];
  readonly rows?: number;
  readonly margin?: Margin;
  /* Margin above the question */
  readonly topMargin?: Margin;
  readonly beforeLabel?: string;
  readonly afterLabel?: string;
  readonly validations?: readonly FormValidation[];
  readonly validationUpdateOn?: 'change' | 'blur' | 'submit';
  /* Related to the custom fields model in `custom-fields.model` */
  readonly customFieldId?: number;
  readonly resourceType?: ResourceTypeApi;
  /**
   * Questions whose values are needed (subscribed) to that affect the current question
   * or the current question affects these dependentQuestions
   */
  readonly dependentQuestions?: readonly FormQuestionKeyApi[];
  /**
   * This enables child questions to be hidden/displayed and disabled/enabled based off
   * the parent question
   */
  readonly questionsRelation?: QuestionRelationApi;
  /* If the child questions are before or after the "main" question */
  readonly questionsPosition?: 'before' | 'after';
  readonly questions?: readonly FormQuestion[];
}

/**
 * The different types of options depending on the type of its parent FormQuestion.
 */
export type FormAnswerOption =
  | AnswerGroupOption
  | AnswerItemOption
  | AnswerInputOption
  | AnswerRadioCardOption;

export interface AnswerOptionBase {
  readonly id: number;
  readonly col?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  readonly value: string;
}

export interface AnswerGroupOption extends AnswerOptionBase {
  readonly answerType: 'group';
  readonly groupName: string;
  readonly options: readonly AnswerItemOption[];
}

export interface AnswerItemOption extends AnswerOptionBase {
  readonly answerType: 'item';
  readonly displayText: string;
}

/**
 * When using this answer option type, the parent question must have a `defaultValue`
 * in the form of an object stating the default value for each option. The default value must
 * not be undefined.
 */
export interface AnswerInputOption extends AnswerOptionBase {
  readonly answerType: 'input';
  readonly name: FormQuestionKeyApi;
  readonly displayText: string;
  readonly type: FormInputTypeApi;
}

export interface AnswerRadioCardOption extends AnswerOptionBase {
  readonly answerType: 'radioCard';
  readonly displayText: string;
  readonly description?: string;
  readonly tag?: string;
  readonly ribbon?: string;
  readonly pictureSrc?: string;
  readonly textAlign?: string;
}

export interface FormValidation {
  readonly id: number;
  readonly validationType: ValidationTypeApi;
  readonly value?: string | number | boolean | RegExp;
  readonly errorMessage: string;
}

/**
 * Used to preload datastore calls so content can be shown as soon as the user arrives at a step.
 * Not necessary if step and form configs are moved to the backend.
 */
export interface PreloadedContent {
  portfolios?: readonly PortfolioItem[];
}

// sections are groups of one or more questions
export enum FormSectionNameApi {
  TITLE = 'title',
  IS_ON_BEHALF = 'is_on_behalf',
  OWNERSHIP_USER = 'ownership_user',
  DESCRIPTION = 'description',
  SKILLS = 'skills',
  JOB_TYPE = 'job_type',
  PROJECT_IS_LOCAL = 'project_is_local',
  PROJECT_LOCATION = 'location',
  PROJECT_BUDGET_TYPE = 'project_budget_type',
  CONTEST_BUDGET = 'contest_budget',
  SUMMARY = 'summary',
  SUBMIT = 'submit',
  // Deloitte
  TIMEFRAME = 'timeframe',
  PROJECT_DETAILS_HEADING = 'project_details_heading',
  // Project type dropdown in project details
  PROJECT_DETAILS_PROJECT_TYPE = 'project_type',
  // Firm contribution dropdown in project details
  PROJECT_DETAILS_CONTRIBUTION = 'project_details_contribution',
  // Business dropdown in project details
  PROJECT_DETAILS_BUSINESS = 'project_details_business',
  // Practice dropdown in project details
  PROJECT_DETAILS_PRACTICE = 'project_details_practice',
  // Offering dropdown in project details
  PROJECT_DETAILS_OFFERING = 'project_details_offering',
  // Industry dropdown in project details
  PROJECT_DETAILS_INDUSTRY = 'project_details_industry',
  // Is subject to ITAR checkbox in project details
  PROJECT_DETAILS_ITAR = 'project_details_itar',
  LIMIT_GIG_WORKER_LEVEL = 'limit_gig_worker_level',
  LIMIT_GROUP_MEMBERS_HEADING = 'limit_group_members_heading',
  LIMIT_GROUP_MEMBERS = 'limit_group_members',
  // Deloitte External
  AUDIENCE = 'audience',
  BILLING = 'billing',
  BUDGET_RECOMMENDER = 'budget_recommender',
  PPMD_APPROVAL = 'ppmd_approval',
  // Empty section purely for formatting purposes
  PLACEHOLDER = 'placeholder',
}

export enum FormHeaderStepNameApi {
  PROJECT_DETAILS = 'project_details',
  SKILLS = 'skills',
  TIMEFRAME = 'timeframe',
  BILLING_DETAILS = 'billing_details',
  DELOITTE_DETAILS = 'deloitte_details',
  OTHER_CONSTRAINTS = 'other_constraints',
  AUDIENCE = 'audience',
  BUDGET = 'budget',
  REVIEW = 'review',
  ENGAGEMENT_PPMD = 'engagement_ppmd',
  CONFIRMATION = 'confirmation',
}

export enum FormInputTypeApi {
  SHORT_TEXT = 'short_text',
  STATIC_TEXT = 'static_text',
  LONG_TEXT = 'long_text',
  MULTI_SELECT = 'multi_select',
  NUMBER = 'number',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  DROPDOWN = 'dropdown',
  LOCATION = 'location',
  OPTION_GROUP = 'option_group',
  BANNER = 'banner',
  /** More customised inputs below */
  // Must have only one `dependentQuestion` that is a `date` input
  START_DATE = 'start_date',
  SKILLS = 'skills',
  DELOITTE_BILLING_CODE = 'deloitte_billing_code',
  DELOITTE_OFFERING_DROPDOWN = 'deloitte_offering_dropdown',
  DELOITTE_BUSINESS_DROPDOWN = 'deloitte_business_dropdown',
  DELOITTE_PRACTICE_DROPDOWN = 'deloitte_practice_dropdown',
  DELOITTE_INDUSTRY_DROPDOWN = 'deloitte_industry_dropdown',
  DELOITTE_BUDGET_RECOMMENDER = 'deloitte_budget_recommender',
  DELOITTE_EXTERNAL_APPROVAL_WIDGET = 'deloitte_external_approval_widget',
  DELOITTE_SIDEBAR_EXTERNAL_APPROVAL_WIDGET = 'deloitte_sidebar_external_approval_widget',
  DELOITTE_PRACTICE_MULTI_SELECT = 'deloitte_practice_multi_select',
  DELOITTE_PROJECT_DESCRIPTION = 'deloitte_project_description',
  DELOITTE_PPMD_EMAIL = 'deloitte_ppmd_email',
  CLIENT_GROUPS_DROPDOWN = 'client_groups_dropdown',
  SUMMARY = 'summary',
  SUBMIT = 'submit',
  // For hidden controls
  NONE = 'none',
}

/**
 * Used to match with a validator in @freelancer/ui/validators
 */
export enum ValidationTypeApi {
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  MIN_LENGTH = 'min_length',
  MAX_LENGTH = 'max_length',
  PATTERN_MATCH = 'pattern_match',
  NOT_PATTERN_MATCH = 'not_pattern_match',
  CURRENT_OR_FUTURE_DATE = 'current_or_future_date',
  DATE_FORMAT = 'date_format',
  REQUIRED = 'required',
}

export enum QuestionRelationApi {
  DISPLAY = 'display',
  DISABLE = 'disable',
}
